import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import RoleService from '../../services/roleService';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import { useAuthSync } from '../../hooks/useAuthSync';
import { MEDICAL_DATA_PERMISSION } from '../../utils/medicalData';
import './Roles.css';

const PERMISSION_OPTIONS = [
  { value: 'reports:viewfinancial', label: 'Ver Reportes Financieros' },
  { value: 'reports:viewappointments', label: 'Ver Reportes de Citas' },
  { value: 'patient:viewmedicaldata', label: 'Ver Datos Medicos del Paciente' },
  { value: 'patient:viewcontactdetails', label: 'Ver Datos de Contacto del Paciente' },
  { value: 'agenda:viewall', label: 'Ver Todas las Agendas' },
  { value: 'appointments:createforothers', label: 'Crear citas para otros' },
  { value: 'treatmentplans:viewall', label: 'Ver todos los Planes de Tratamiento' }
];

const DEFAULT_ROLES = [
  { id: 'default-propietario', name: 'Propietario', isDefault: true },
  { id: 'default-administrador', name: 'Administrador', isDefault: true }
];

const Roles = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();
  const [roles, setRoles] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);

  const permissionLabelMap = useMemo(() => {
    return PERMISSION_OPTIONS.reduce((acc, permission) => {
      acc[permission.value] = permission.label;
      return acc;
    }, {});
  }, []);

  const loadRoles = useCallback(async () => {
    try {
      setInitialLoading(true);
      const token = auth.user?.access_token;
      const response = await RoleService.getAllRoles(token);
      setRoles(response.data || []);
    } catch (error) {
      showError('Error al cargar los roles');
    } finally {
      setInitialLoading(false);
    }
  }, [auth.user?.access_token, showError]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  useEffect(() => {
    if (location.state?.message && location.state.messageId) {
      const storageKey = `rolesToast:${location.state.messageId}`;

      if (!sessionStorage.getItem(storageKey)) {
        showSuccess(location.state.message);
        sessionStorage.setItem(storageKey, 'shown');
      }

      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state?.message, location.state?.messageId, location.pathname, navigate, showSuccess]);

  const handleAddRole = () => {
    navigate('/settings/roles/new');
  };

  const handleEditRole = (roleId) => {
    navigate(`/settings/roles/${roleId}/edit`);
  };

  const { isMedicalDataEnabled } = useAuthSync();
  const medicalDataEnabled = isMedicalDataEnabled();

  const displayRoles = useMemo(() => {
    // Without medical data the permission has no effect, so it is not shown
    const visibleRoles = medicalDataEnabled
      ? roles
      : roles.map(role => ({
        ...role,
        permissions: (role.permissions || []).filter(permission => permission !== MEDICAL_DATA_PERMISSION)
      }));
    return [...DEFAULT_ROLES, ...visibleRoles];
  }, [roles, medicalDataEnabled]);

  if (initialLoading) {
    return (
      <div className="roles-container">
        <div className="roles-loading">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="roles-container">
      <div className="roles-header">
        <div className="header-content">
          <h1>Roles y Permisos</h1>
          <p>Configura los roles disponibles para el equipo</p>
        </div>
        <button className="btn-primary" onClick={handleAddRole}>
          Agregar Rol
        </button>
      </div>
      <div className="roles-table-container">
        {displayRoles.length === 0 ? (
          <div className="no-roles">
            <p>No se han creado roles aun</p>
          </div>
        ) : (
          <>
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Rol</th>
                  <th>Permisos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {displayRoles.map((role) => (
                  <tr key={role.id}>
                    <td className="role-name">
                      <div className="role-details">
                        <span className="name">{role.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="role-permissions-list">
                        {role.isDefault ? (
                          <span className="permission-chip">Todos los permisos</span>
                        ) : (role.permissions || []).length === 0 ? (
                          <span className="muted">Sin permisos</span>
                        ) : (
                          role.permissions.map((permission) => (
                            <span key={permission} className="permission-chip">
                              {permissionLabelMap[permission] || permission}
                            </span>
                          ))
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="role-actions">
                        <button
                          type="button"
                          className="role-action-btn"
                          onClick={() => handleEditRole(role.id)}
                          disabled={role.isDefault}
                        >
                          Editar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="roles-mobile-cards">
              {displayRoles.map((role) => (
                <div key={role.id} className="role-card">
                  <div className="role-card-header">
                    <div className="role-card-title">{role.name}</div>
                  </div>
                  <div className="role-card-content">
                    <div className="card-detail">
                      <strong>Permisos</strong>
                      {role.isDefault ? (
                        <span className="permission-chip">Todos los permisos</span>
                      ) : (role.permissions || []).length === 0 ? (
                        <span className="muted">Sin permisos</span>
                      ) : (
                        <div className="role-permissions-list">
                          {role.permissions.map((permission) => (
                            <span key={permission} className="permission-chip">
                              {permissionLabelMap[permission] || permission}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="role-card-actions">
                    <button
                      type="button"
                      className="role-action-btn"
                      onClick={() => handleEditRole(role.id)}
                      disabled={role.isDefault}
                    >
                      Editar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default Roles;
